import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";

export type CadenceOnboardingStepId =
  | "project"
  | "composer"
  | "timeline";

interface TourStep {
  id: CadenceOnboardingStepId;
  title: string;
  body: string;
  selectors(): string[];
  preferredPlacement: "top" | "right" | "bottom" | "left";
}

interface CadenceOnboardingTourProps {
  active: boolean;
  stepIndex: number;
  hasProjects: boolean;
  onStepChange(stepIndex: number): void;
  onDismiss(): void;
  onUnavailable(): void;
  onComplete(): void;
}

interface TargetGeometry {
  rect: DOMRect;
  bubbleLeft: number;
  bubbleTop: number;
  placement: TourStep["preferredPlacement"];
}

const VIEWPORT_PADDING = 12;
const TARGET_GAP = 12;
const TARGET_PADDING = 5;

const steps: TourStep[] = [
  {
    id: "project",
    title: "研究项目",
    body: "在这里创建和切换研究项目，所有工作都围绕项目展开。",
    selectors: () =>
      window.matchMedia("(max-width: 800px)").matches
        ? ['[data-testid="mobile-project-menu"]']
        : [".project-sidebar"],
    preferredPlacement: "right",
  },
  {
    id: "composer",
    title: "发起研究请求",
    body: "在这里提出下一项可验证的研究问题；发送后，团队才会开始研究。",
    selectors: () => ['[data-testid="composer"]'],
    preferredPlacement: "top",
  },
  {
    id: "timeline",
    title: "研究过程与产物",
    body: "在这里查看真实请求的进展，并按需展开 Agent 活动、证据和产物。",
    selectors: () => ['[data-testid="request-timeline"]'],
    preferredPlacement: "right",
  },
];

function isVisible(element: HTMLElement): boolean {
  const rect = element.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return false;
  let current: HTMLElement | null = element;
  while (current) {
    const style = window.getComputedStyle(current);
    if (
      style.display === "none" ||
      style.visibility === "hidden" ||
      Number(style.opacity || "1") === 0
    ) {
      return false;
    }
    current = current.parentElement;
  }
  return true;
}

function findTarget(step: TourStep): HTMLElement | undefined {
  for (const selector of step.selectors()) {
    const candidates = document.querySelectorAll<HTMLElement>(selector);
    const visible = Array.from(candidates).find(isVisible);
    if (visible) return visible;
  }
  return undefined;
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(Math.max(value, minimum), Math.max(minimum, maximum));
}

function visibleViewportBottom(): number {
  return window.innerHeight;
}

function clipTargetRect(rect: DOMRect, viewportBottom: number): DOMRect {
  const top = clamp(rect.top, VIEWPORT_PADDING, viewportBottom);
  const bottom = clamp(rect.bottom, top, viewportBottom);
  return new DOMRect(rect.left, top, rect.width, bottom - top);
}

function positionBubble(
  rect: DOMRect,
  preferredPlacement: TourStep["preferredPlacement"],
  bubbleWidth: number,
  bubbleHeight: number,
  viewportBottom = window.innerHeight,
): Omit<TargetGeometry, "rect"> {
  const viewportWidth = window.innerWidth;
  const placements: TourStep["preferredPlacement"][] = [
    preferredPlacement,
    preferredPlacement === "left" || preferredPlacement === "right"
      ? "top"
      : "right",
    preferredPlacement === "top" ? "bottom" : "left",
    "bottom",
  ];

  const fits = (placement: TourStep["preferredPlacement"]) => {
    if (placement === "right") {
      return rect.right + TARGET_GAP + bubbleWidth <= viewportWidth;
    }
    if (placement === "left") {
      return rect.left - TARGET_GAP - bubbleWidth >= 0;
    }
    if (placement === "top") {
      return rect.top - TARGET_GAP - bubbleHeight >= 0;
    }
    return rect.bottom + TARGET_GAP + bubbleHeight <= viewportBottom;
  };

  const placement = placements.find(fits) ?? preferredPlacement;
  let bubbleLeft = rect.left;
  let bubbleTop = rect.top;

  if (placement === "right") {
    bubbleLeft = rect.right + TARGET_GAP;
    bubbleTop = rect.top + Math.min(32, Math.max(0, rect.height / 4));
  } else if (placement === "left") {
    bubbleLeft = rect.left - TARGET_GAP - bubbleWidth;
    bubbleTop = rect.top + Math.min(32, Math.max(0, rect.height / 4));
  } else if (placement === "top") {
    bubbleLeft = rect.left + rect.width / 2 - bubbleWidth / 2;
    bubbleTop = rect.top - TARGET_GAP - bubbleHeight;
  } else {
    bubbleLeft = rect.left + rect.width / 2 - bubbleWidth / 2;
    bubbleTop = rect.bottom + TARGET_GAP;
  }

  return {
    bubbleLeft: clamp(
      bubbleLeft,
      VIEWPORT_PADDING,
      viewportWidth - bubbleWidth - VIEWPORT_PADDING,
    ),
    bubbleTop: clamp(
      bubbleTop,
      VIEWPORT_PADDING,
      viewportBottom - bubbleHeight - VIEWPORT_PADDING,
    ),
    placement,
  };
}

export function CadenceOnboardingTour({
  active,
  stepIndex,
  hasProjects,
  onStepChange,
  onDismiss,
  onUnavailable,
  onComplete,
}: CadenceOnboardingTourProps) {
  const step = steps[stepIndex];
  const bubbleRef = useRef<HTMLElement>(null);
  const primaryButtonRef = useRef<HTMLButtonElement>(null);
  const skipButtonRef = useRef<HTMLButtonElement>(null);
  const targetRef = useRef<HTMLElement>();
  const originalFocusRef = useRef<HTMLElement>();
  const restoreFocusOnDismissRef = useRef(true);
  const focusedStepRef = useRef<number>();
  const missingTimerRef = useRef<number>();
  const resizeObserverRef = useRef<ResizeObserver>();
  const [geometry, setGeometry] = useState<TargetGeometry>();

  const clearMissingTimer = useCallback(() => {
    if (missingTimerRef.current === undefined) return;
    window.clearTimeout(missingTimerRef.current);
    missingTimerRef.current = undefined;
  }, []);

  const clearTarget = useCallback(() => {
    const target = targetRef.current;
    if (target?.dataset.tourAnchor === step?.id) {
      delete target.dataset.tourAnchor;
    }
    if (target) resizeObserverRef.current?.unobserve(target);
    targetRef.current = undefined;
  }, [step?.id]);

  const assignTarget = useCallback(
    (target: HTMLElement) => {
      if (targetRef.current === target) return;
      clearTarget();
      targetRef.current = target;
      target.dataset.tourAnchor = step?.id;
      resizeObserverRef.current?.observe(target);
    },
    [clearTarget, step?.id],
  );

  const updateGeometry = useCallback(
    (scrollTarget = false) => {
      if (!active || !step) return;
      if (
        document.querySelector(
          ".project-dialog-backdrop, .research-state-backdrop",
        )
      ) {
        clearMissingTimer();
        clearTarget();
        setGeometry(undefined);
        return;
      }

      const target =
        targetRef.current?.isConnected && isVisible(targetRef.current)
          ? targetRef.current
          : findTarget(step);
      if (!target) {
        clearTarget();
        setGeometry(undefined);
        if (missingTimerRef.current === undefined) {
          missingTimerRef.current = window.setTimeout(() => {
            missingTimerRef.current = undefined;
            onUnavailable();
          }, 1_200);
        }
        return;
      }

      clearMissingTimer();
      assignTarget(target);

      if (scrollTarget) {
        const currentRect = target.getBoundingClientRect();
        const viewportBottom = visibleViewportBottom();
        const outsideViewport =
          currentRect.bottom < 72 ||
          currentRect.top > viewportBottom - 72 ||
          currentRect.bottom > viewportBottom;
        if (outsideViewport) {
          target.scrollIntoView({
            block: "center",
            inline: "nearest",
            behavior: window.matchMedia("(prefers-reduced-motion: reduce)")
              .matches
              ? "auto"
              : "smooth",
          });
        }
      }

      const measuredTarget =
        target.isConnected &&
        targetRef.current === target &&
        isVisible(target)
          ? target
          : findTarget(step);
      if (!measuredTarget) {
        clearTarget();
        setGeometry(undefined);
        if (missingTimerRef.current === undefined) {
          missingTimerRef.current = window.setTimeout(() => {
            missingTimerRef.current = undefined;
            onUnavailable();
          }, 1_200);
        }
        return;
      }
      clearMissingTimer();
      assignTarget(measuredTarget);
      const viewportBottom = visibleViewportBottom();
      const rect = clipTargetRect(
        measuredTarget.getBoundingClientRect(),
        viewportBottom,
      );
      const bubbleWidth =
        bubbleRef.current?.offsetWidth ??
        Math.min(320, window.innerWidth - VIEWPORT_PADDING * 2);
      const bubbleHeight = bubbleRef.current?.offsetHeight ?? 176;
      const preferredPlacement =
        step.id === "project" &&
        window.matchMedia("(max-width: 800px)").matches
          ? "bottom"
          : step.preferredPlacement;
      setGeometry({
        rect,
        ...positionBubble(
          rect,
          preferredPlacement,
          bubbleWidth,
          bubbleHeight,
          viewportBottom,
        ),
      });
    },
    [
      active,
      assignTarget,
      clearMissingTimer,
      clearTarget,
      onUnavailable,
      step,
    ],
  );

  useEffect(() => {
    if (!active) return;
    restoreFocusOnDismissRef.current = true;
    originalFocusRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : undefined;
    return () => {
      if (!restoreFocusOnDismissRef.current) return;
      const original = originalFocusRef.current;
      const fallback = document.querySelector<HTMLElement>(
        '[data-testid="onboarding-replay"]',
      );
      window.setTimeout(() => {
        if (original?.isConnected && original !== document.body) {
          original.focus();
        } else {
          fallback?.focus();
        }
      }, 0);
    };
  }, [active]);

  useLayoutEffect(() => {
    if (!active || !step) {
      clearTarget();
      setGeometry(undefined);
      return;
    }

    focusedStepRef.current = undefined;
    const handleChange = () => updateGeometry();
    const resizeObserver = new ResizeObserver(handleChange);
    resizeObserverRef.current = resizeObserver;
    updateGeometry(true);
    const mutationObserver = new MutationObserver((mutations) => {
      const relevantMutation = mutations.some((mutation) => {
        const target =
          mutation.target instanceof Element
            ? mutation.target
            : mutation.target.parentElement;
        return !target?.closest(
          ".cadence-onboarding-bubble, .cadence-onboarding-spotlight",
        );
      });
      if (relevantMutation) handleChange();
    });
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "aria-hidden"],
    });
    window.addEventListener("resize", handleChange);
    window.addEventListener("scroll", handleChange, true);
    document.addEventListener("transitionend", handleChange, true);

    return () => {
      resizeObserver.disconnect();
      resizeObserverRef.current = undefined;
      mutationObserver.disconnect();
      window.removeEventListener("resize", handleChange);
      window.removeEventListener("scroll", handleChange, true);
      document.removeEventListener("transitionend", handleChange, true);
      clearMissingTimer();
      clearTarget();
    };
  }, [active, clearMissingTimer, clearTarget, step, updateGeometry]);

  useLayoutEffect(() => {
    if (!geometry) return;
    updateGeometry();
  }, [geometry?.rect.width, geometry?.rect.height, updateGeometry]);

  useEffect(() => {
    if (!active) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      if (document.querySelector(".dna-entry-transition")) return;
      event.preventDefault();
      onDismiss();
    };
    const handlePointerDown = (event: PointerEvent) => {
      const clicked = event.target;
      if (!(clicked instanceof Node)) return;
      const creatingInitialProject = step?.id === "project" && !hasProjects;
      if (
        bubbleRef.current?.contains(clicked) ||
        targetRef.current?.contains(clicked) ||
        (clicked instanceof Element &&
          clicked.closest(".dna-entry-transition")) ||
        (creatingInitialProject &&
          clicked instanceof Element &&
          clicked.closest('[data-testid="mobile-project-menu-panel"]')) ||
        (clicked instanceof Element &&
          clicked.closest(
            ".project-dialog-backdrop, .research-state-backdrop",
          ))
      ) {
        return;
      }
      // A researcher deliberately acted on the workspace behind the guide.
      // Let the browser keep that control's focus instead of restoring the
      // guide trigger after the tour unmounts.
      restoreFocusOnDismissRef.current = false;
      onDismiss();
    };
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("pointerdown", handlePointerDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("pointerdown", handlePointerDown);
    };
  }, [active, hasProjects, onDismiss, step?.id]);

  useEffect(() => {
    if (!geometry || focusedStepRef.current === stepIndex) return;
    focusedStepRef.current = stepIndex;
    if (primaryButtonRef.current?.disabled) {
      skipButtonRef.current?.focus();
    } else {
      primaryButtonRef.current?.focus();
    }
  }, [geometry, hasProjects, stepIndex]);

  if (!active || !step || !geometry) return null;

  const waitingForProject = step.id === "project" && !hasProjects;
  const lastStep = stepIndex === steps.length - 1;
  const spotlightLeft = Math.max(4, geometry.rect.left - TARGET_PADDING);
  const spotlightTop = Math.max(4, geometry.rect.top - TARGET_PADDING);
  const spotlightRight = Math.min(
    window.innerWidth - 4,
    geometry.rect.right + TARGET_PADDING,
  );
  const spotlightBottom = Math.min(
    window.innerHeight - 4,
    geometry.rect.bottom + TARGET_PADDING,
  );
  const spotlightStyle = {
    left: spotlightLeft,
    top: spotlightTop,
    width: Math.max(0, spotlightRight - spotlightLeft),
    height: Math.max(0, spotlightBottom - spotlightTop),
  } satisfies CSSProperties;
  const bubbleStyle = {
    left: geometry.bubbleLeft,
    top: geometry.bubbleTop,
  } satisfies CSSProperties;

  return (
    <>
      <div
        className="cadence-onboarding-spotlight"
        data-testid="onboarding-spotlight"
        style={spotlightStyle}
        aria-hidden="true"
      />
      <section
        className="cadence-onboarding-bubble"
        data-placement={geometry.placement}
        data-testid="onboarding-tour"
        ref={bubbleRef}
        role="dialog"
        aria-labelledby="cadence-onboarding-title"
        aria-describedby="cadence-onboarding-body"
        style={bubbleStyle}
      >
        <div className="cadence-onboarding-head">
          <span data-testid="onboarding-progress">
            步骤 {stepIndex + 1} / {steps.length}
          </span>
          <button
            data-testid="onboarding-skip"
            ref={skipButtonRef}
            type="button"
            onClick={onDismiss}
          >
            跳过
          </button>
        </div>
        <h2 id="cadence-onboarding-title" data-testid="onboarding-title">
          {step.title}
        </h2>
        <p id="cadence-onboarding-body">{step.body}</p>
        {waitingForProject && (
          <p className="cadence-onboarding-note" role="note">
            创建项目后，引导会自动继续。
          </p>
        )}
        <div className="cadence-onboarding-actions">
          {stepIndex > 0 && (
            <button
              className="cadence-onboarding-back"
              type="button"
              onClick={() => onStepChange(stepIndex - 1)}
            >
              上一步
            </button>
          )}
          <button
            className="cadence-onboarding-next"
            data-testid="onboarding-next"
            ref={primaryButtonRef}
            type="button"
            disabled={waitingForProject}
            onClick={() => {
              if (lastStep) {
                onComplete();
              } else {
                onStepChange(stepIndex + 1);
              }
            }}
          >
            {waitingForProject ? "创建项目后继续" : lastStep ? "完成" : "下一步"}
          </button>
        </div>
      </section>
      <span
        className="visually-hidden"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        步骤 {stepIndex + 1} / {steps.length}，{step.title}。{step.body}
      </span>
    </>
  );
}
